import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import { useCurrentUser, useOperator } from "../../../../globals/hooks";
import AddCreditCardDialog from "../../../globals/AddCreditCardDialog";
import { PaymentMethodCardSelect } from "components/confirm/form/components/PaymentMethodBlock/components";
import PayButton from "./PayButton";
import PaymentElementChargeCustomerBlock from "./PaymentElementChargeCustomerBlock";
import type { PaymentLinkSetupContext } from "../paymentLinkSetupReturn";
import type { SavedPaymentMethod } from "components/payment/PaymentMethodSetup/types";
import {
  readProfileSetupReturn,
  removeSetupReturnParams,
} from "components/profile/paymentMethodSetupReturn";

type AuthenticatedChargeCustomerBlockProps = {
  totalDue: string;
  selectedPaymentMethodId: string;
  onSetPaymentMethod: (handleAuthenticatedPay) => void;
  isLoading: boolean;
  onPayClick: () => void;
  companyId?: string;
  paymentSetupContext?: PaymentLinkSetupContext;
  onCreatePayment?: (paymentMethodId: string, clientSecret?: string) => void;
};

function AuthenticatedChargeCustomerBlock(
  props: AuthenticatedChargeCustomerBlockProps
) {
  const {
    totalDue,
    selectedPaymentMethodId,
    onSetPaymentMethod,
    isLoading,
    onPayClick,
    companyId,
    paymentSetupContext,
    onCreatePayment,
  } = props;

  // hooks
  const currentUser = useCurrentUser();
  const { operator } = useOperator();
  const location = useLocation();
  const history = useHistory();

  // state
  const [addCreditCardDialogOpen, setAddCreditCardDialogOpen] = useState(false);
  const [paymentElementContext, setPaymentElementContext] = useState<{
    operatorId: string;
    payerId: string;
  } | null>(null);
  const [payerType, setPayerType] = useState("personal");
  const [savedMethod, setSavedMethod] = useState<SavedPaymentMethod>();
  const payerId = currentUser?.id;
  const isCurrentPayer =
    !paymentElementContext ||
    (paymentElementContext.operatorId === operator.id &&
      paymentElementContext.payerId === payerId);
  const [returnVersion, setReturnVersion] = useState(0);
  const setupReturn = useMemo(
    () =>
      readProfileSetupReturn(
        operator.id,
        payerId,
        location.pathname,
        location.search
      ),
    [operator.id, payerId, location.pathname, location.search, returnVersion]
  );

  useEffect(() => {
    setAddCreditCardDialogOpen(false);
    setPaymentElementContext(null);
    setSavedMethod(undefined);
  }, [operator.id, payerId, location.pathname]);

  useEffect(() => {
    if (setupReturn) {
      setPaymentElementContext({
        operatorId: setupReturn.operatorId,
        payerId: setupReturn.payerId,
      });
      setAddCreditCardDialogOpen(true);
    }
  }, [setupReturn]);

  const contactMethods =
    savedMethod &&
    !currentUser?.paymentMethods?.some((method) => method.id === savedMethod.id)
      ? [
          ...(currentUser?.paymentMethods || []),
          { ...savedMethod, billingDetails: null, linkedPassenger: null },
        ]
      : currentUser?.paymentMethods;
  const companyMethods = companyId ? currentUser?.company?.paymentMethods : [];
  const paymentMethodsEnabled =
    contactMethods?.length || companyMethods?.length;

  const inlineEnabled = operator.enablePaymentElement && !!paymentSetupContext;
  const companySelected = payerType === "company" && !!companyMethods?.length;
  const selectedPayerId = companySelected ? companyId : payerId;

  return (
    <Box display="flex" flexDirection="column">
      {inlineEnabled && (
        <>
          {!!companyMethods?.length && (
            <ToggleButtonGroup
              disabled={isLoading || addCreditCardDialogOpen}
              exclusive
              value={companySelected ? "company" : "personal"}
              onChange={(_, value) => {
                if (value) setPayerType(value);
              }}
              sx={{ mb: 2 }}
              aria-label="Card owner"
            >
              <ToggleButton value="personal">Personal</ToggleButton>
              <ToggleButton value="company">Company</ToggleButton>
            </ToggleButtonGroup>
          )}
          {companySelected && (
            <Typography sx={{ mb: 2 }}>
              Select a saved company card. Add new cards under Personal.
            </Typography>
          )}
          <PaymentElementChargeCustomerBlock
            key={JSON.stringify([selectedPayerId, savedMethod?.id])}
            operatorId={operator.id}
            currency={operator.settings?.currencyCode || "USD"}
            context={{ ...paymentSetupContext, payerId: selectedPayerId }}
            authenticatedContactId={payerId}
            savedPaymentMethods={
              companySelected ? companyMethods : contactMethods || []
            }
            savedCardsOnly={companySelected}
            billingDetails={{
              name: `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim(),
              email: currentUser?.email,
              phone: currentUser?.mobilePhone,
            }}
            onCreatePayment={onCreatePayment}
            totalDue={totalDue}
            isLoading={isLoading || addCreditCardDialogOpen}
          />
        </>
      )}
      {!inlineEnabled && !!paymentMethodsEnabled && (
        <Box mb={2}>
          <PaymentMethodCardSelect
            existingContactPaymentMethods={contactMethods}
            onSetPaymentMethod={onSetPaymentMethod}
            selectedPaymentMethodId={selectedPaymentMethodId}
            {...(companyId && {
              existingCompanyPaymentMethods: companyMethods,
            })}
          />
        </Box>
      )}
      {!inlineEnabled && (
        <Box mb={3} display="flex" justifyContent="space-between">
          <PayButton
            isDisabled={
              isLoading ||
              addCreditCardDialogOpen ||
              !(paymentMethodsEnabled || selectedPaymentMethodId)
            }
            isLoading={isLoading}
            onClick={onPayClick}
            totalDue={totalDue}
          />
          <Button
            color="primary"
            variant="outlined"
            disabled={!currentUser || isLoading}
            onClick={() => {
              setPayerType("personal");
              setPaymentElementContext(
                operator.enablePaymentElement || setupReturn
                  ? { operatorId: operator.id, payerId }
                  : null
              );
              setAddCreditCardDialogOpen(true);
            }}
          >
            Add a New Card
          </Button>
        </Box>
      )}
      {currentUser && (
        <AddCreditCardDialog
          open={addCreditCardDialogOpen && isCurrentPayer}
          setAddCreditCardDialogOpen={setAddCreditCardDialogOpen}
          onSetPaymentMethod={onSetPaymentMethod}
          onSavedPaymentMethod={setSavedMethod}
          paymentElementContext={paymentElementContext}
          paymentElementReturn={setupReturn}
          onPaymentElementReturnCleared={() => {
            setReturnVersion((version) => version + 1);
            if (setupReturn)
              history.replace({
                ...location,
                search: removeSetupReturnParams(location.search),
              });
          }}
        />
      )}
    </Box>
  );
}

export default AuthenticatedChargeCustomerBlock;
